/**
 * MIDI SMF → lab ClipIR + instruments.
 * Uses @tonejs/midi for parsing; maps notes/CC/bend into tracker cells.
 */
import { Midi } from "@tonejs/midi";
import type { Instrument, InstrumentSlot } from "../types";
import {
  MAX_INSTRUMENT_SLOT_COUNT,
  clampInstrumentSlotCount,
} from "../types";
import {
  createDefaultDrumSf2Patch,
  isDistortionGuitarGm,
  isElectricGuitarGm,
  sf2Instrument,
  sf2PatchForGmMelodic,
  synthInstrument,
} from "../kernel/drumKit";
import { defaultPresetForSlot } from "../kernel/defaultPresets";
import { presetForGmProgram } from "./gmPresetMap";
import { CUT_OPEN_HOLD } from "./fx";
import { MAX_CLIP_STEPS } from "./parsePattern";
import { repairSmfTrackChunkLengths } from "./repairSmf";
import {
  createEmptyClip,
  emptyCell,
  secondsPerStep,
  type ClipIR,
  type NoteFx,
  type PatternCell,
} from "./types";

const HOLD_MIN_STEPS = 1.5;
const DEFAULT_BEND_RANGE_SEMI = 2;
const BEND_RATE_THRESHOLD = 0.35;
const FX_BUDGET = 4;
const VIBRATO_SPEED_NIBBLE = 6;

export type ImportMidiResult = {
  clip: ClipIR;
  instruments: Instrument[];
  warnings: string[];
};

type TimedValue = { time: number; value: number };

type SourceNote = {
  channel: number;
  midi: number;
  time: number;
  duration: number;
  velocity: number;
  program: number;
  trackName: string;
  family: string;
  percussion: boolean;
};

type LabKind = "synth" | "drums" | "sf2";

type ChannelPlan = {
  channel: number;
  kind: LabKind;
  name: string;
  program: number;
  notes: SourceNote[];
  cc1: TimedValue[];
  cc10: TimedValue[];
  cc74: TimedValue[];
  bends: TimedValue[];
};

/** Parse SMF bytes (or a pre-parsed Midi) into a lab project clip + instruments. */
export function importMidiToProject(data: ArrayBuffer | Uint8Array | Midi): ImportMidiResult {
  const warnings: string[] = [];
  let midi: Midi;
  if (data instanceof Midi) {
    midi = data;
  } else {
    const raw = data instanceof Uint8Array ? data : new Uint8Array(data);
    const repaired = repairSmfTrackChunkLengths(raw);
    for (const warning of repaired.warnings) {
      warnings.push(warning);
    }
    try {
      midi = new Midi(repaired.bytes);
    } catch (error) {
      throw new Error(`Invalid MIDI file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  const bpmRaw = midi.header.tempos[0]?.bpm ?? 120;
  const bpm = Math.min(400, Math.max(20, Math.round(bpmRaw)));
  if (Math.abs(bpm - bpmRaw) > 0.5) {
    warnings.push(`Tempo clamped to ${bpm} BPM (file had ${bpmRaw.toFixed(1)}).`);
  }

  const durationSec = Math.max(midi.duration, 0.01);
  const stepDivision = chooseStepDivision(midi, bpm, durationSec);
  const stepSec = secondsPerStep({ bpm, stepDivision });
  let steps = Math.max(1, Math.ceil(durationSec / stepSec));
  if (steps > MAX_CLIP_STEPS) {
    warnings.push(`Pattern truncated to ${MAX_CLIP_STEPS} steps (file needed ${steps}).`);
    steps = MAX_CLIP_STEPS;
  }

  const channels = collectChannels(midi);
  if (channels.length === 0) {
    warnings.push("MIDI file has no notes.");
  }

  type VoiceTrack = {
    slot: InstrumentSlot;
    kind: LabKind;
    name: string;
    channel: number;
    program: number;
    cells: PatternCell[];
    /** End time (sec) of last assigned note — synth voice allocation. */
    busyUntil: number;
  };

  const voices: VoiceTrack[] = [];
  let fxDropped = 0;

  function allocVoice(plan: ChannelPlan, noteStart: number, noteEnd: number, onStep: number): VoiceTrack | undefined {
    const same = voices.filter(v => v.channel === plan.channel && v.kind === plan.kind);

    if (plan.kind === "drums" || plan.kind === "sf2") {
      // Prefer a track whose onStep cell is free (SF2/drums are poly across time,
      // but each pattern cell still holds one note).
      const free = same.find(v => {
        const cell = v.cells[onStep];
        return !cell?.note && !cell?.noteOff;
      });
      if (free) {
        free.busyUntil = Math.max(free.busyUntil, noteEnd);
        return free;
      }
      if (voices.length < MAX_INSTRUMENT_SLOT_COUNT) {
        const voice: VoiceTrack = {
          slot: voices.length as InstrumentSlot,
          kind: plan.kind,
          name: same.length === 0 ? plan.name : `${plan.name} ${same.length + 1}`,
          channel: plan.channel,
          program: plan.program,
          cells: Array.from({ length: steps }, () => emptyCell()),
          busyUntil: noteEnd,
        };
        voices.push(voice);
        return voice;
      }
      warnings.push(`Track limit (${MAX_INSTRUMENT_SLOT_COUNT}); dropped chord voice on ch ${plan.channel + 1}.`);
      return undefined;
    }

    // Synth: prefer a free voice on this channel, else new, else steal earliest-ending.
    const free = same.find(v => v.busyUntil <= noteStart + 1e-6);
    if (free) {
      free.busyUntil = noteEnd;
      return free;
    }
    if (voices.length < MAX_INSTRUMENT_SLOT_COUNT) {
      const voice: VoiceTrack = {
        slot: voices.length as InstrumentSlot,
        kind: "synth",
        name: same.length === 0 ? plan.name : `${plan.name} ${same.length + 1}`,
        channel: plan.channel,
        program: plan.program,
        cells: Array.from({ length: steps }, () => emptyCell()),
        busyUntil: noteEnd,
      };
      voices.push(voice);
      return voice;
    }
    const pool = same.length > 0 ? same : voices.filter(v => v.kind === "synth");
    if (pool.length === 0) {
      warnings.push(`No free track for channel ${plan.channel + 1} note; skipped.`);
      return undefined;
    }
    pool.sort((a, b) => a.busyUntil - b.busyUntil);
    const stolen = pool[0]!;
    stolen.busyUntil = noteEnd;
    return stolen;
  }

  for (const plan of channels) {
    const sorted = [...plan.notes].sort((a, b) => a.time - b.time || a.midi - b.midi);
    for (const note of sorted) {
      const noteEnd = note.time + Math.max(0.001, note.duration);
      if (note.time >= steps * stepSec) {
        continue;
      }
      const onStep = Math.min(steps - 1, Math.max(0, Math.floor(note.time / stepSec)));
      const voice = allocVoice(plan, note.time, noteEnd, onStep);
      if (!voice) {
        continue;
      }

      const frac = note.time / stepSec - Math.floor(note.time / stepSec);
      const endTime = Math.min(noteEnd, steps * stepSec);
      const durSteps = (endTime - note.time) / stepSec;
      const offStep = Math.min(
        steps - 1,
        Math.max(onStep, Math.round(endTime / stepSec)),
      );

      const fx: NoteFx[] = [];
      const hold = durSteps >= HOLD_MIN_STEPS;
      if (hold) {
        fx.push({ kind: "cut", value: CUT_OPEN_HOLD });
      } else {
        const cutVal = Math.min(0xff, Math.max(1, Math.round(durSteps * 16)));
        fx.push({ kind: "cut", value: cutVal });
      }

      const delay = Math.round(frac * 256);
      if (delay > 2 && delay < 256) {
        fx.push({ kind: "noteDelay", value: delay });
      }

      // CC at onset
      const mod = sampleTimeline(plan.cc1, note.time, 0);
      const pan = sampleTimeline(plan.cc10, note.time, 64 / 127);
      const bright = sampleTimeline(plan.cc74, note.time, 64 / 127);
      pushCcFx(fx, mod, pan, bright);

      // Bend over first step of the note
      const bendFx = bendToSlideFx(plan.bends, note.time, Math.min(note.time + stepSec, endTime), stepSec);
      if (bendFx) {
        fx.push(bendFx);
      }

      const packed = packFx(fx);
      if (packed.dropped > 0) {
        fxDropped += packed.dropped;
      }

      let midiNote = Math.max(0, Math.min(127, Math.round(note.midi)));
      // Drum tracks keep GM percussion note numbers (SF2 kit maps 35–…).

      const cell = voice.cells[onStep]!;
      // If cell already has a note (poly steal / overlap on same voice), replace.
      cell.note = midiNote;
      cell.noteOff = undefined;
      cell.volume = Math.min(1, Math.max(0.05, note.velocity));
      cell.fx = packed.fx.length > 0 ? packed.fx : undefined;

      if (hold && offStep > onStep) {
        const offCell = voice.cells[offStep]!;
        if (offCell.note === undefined) {
          offCell.noteOff = true;
        }
      }

      // Mid-hold CC / bend patches on empty rows
      if (hold) {
        let lastMod = mod;
        let lastPan = pan;
        let lastBright = bright;
        for (let step = onStep + 1; step < offStep; step++) {
          const t0 = step * stepSec;
          const t1 = t0 + stepSec;
          if (t0 >= endTime) {
            break;
          }
          const cellAt = voice.cells[step]!;
          if (cellAt.note !== undefined || cellAt.noteOff) {
            continue;
          }
          const patch: NoteFx[] = [];
          const m = sampleTimeline(plan.cc1, t0, lastMod);
          const p = sampleTimeline(plan.cc10, t0, lastPan);
          const b = sampleTimeline(plan.cc74, t0, lastBright);
          if (Math.abs(m - lastMod) > 0.04) {
            pushVibrato(patch, m);
            lastMod = m;
          }
          if (Math.abs(p - lastPan) > 0.04) {
            pushPan(patch, p);
            lastPan = p;
          }
          if (Math.abs(b - lastBright) > 0.04) {
            pushFilter(patch, b);
            lastBright = b;
          }
          const slide = bendToSlideFx(plan.bends, t0, Math.min(t1, endTime), stepSec);
          if (slide) {
            patch.push(slide);
          }
          if (patch.length === 0) {
            continue;
          }
          const packedPatch = packFx(patch);
          if (packedPatch.dropped > 0) {
            fxDropped += packedPatch.dropped;
          }
          if (packedPatch.fx.length > 0) {
            cellAt.fx = packedPatch.fx;
          }
        }
      }
    }
  }

  if (fxDropped > 0) {
    warnings.push(`Dropped ${fxDropped} FX (4-per-cell budget).`);
  }

  const trackCount = clampInstrumentSlotCount(Math.max(1, voices.length));
  const clip = createEmptyClip({ bpm, steps, stepDivision, trackCount });
  const pattern = clip.patterns[0]!;
  pattern.name = midi.name?.trim() || "MIDI Import";

  const instruments: Instrument[] = [];
  for (let i = 0; i < trackCount; i++) {
    const voice = voices[i];
    const track = pattern.tracks[i]!;
    if (!voice) {
      instruments.push(synthInstrument(defaultPresetForSlot(i as InstrumentSlot)));
      continue;
    }
    track.name = voice.name.slice(0, 24);
    track.cells = voice.cells;
    instruments.push(instrumentForKind(voice.kind, voice.program, voice.name, i as InstrumentSlot));
  }

  // Pad unused slots if we created fewer voices than trackCount somehow
  while (instruments.length < trackCount) {
    const slot = instruments.length as InstrumentSlot;
    instruments.push(synthInstrument(defaultPresetForSlot(slot)));
  }

  return { clip, instruments, warnings };
}

function instrumentForKind(
  kind: LabKind,
  program: number,
  name: string,
  slot: InstrumentSlot,
): Instrument {
  if (kind === "drums") {
    return sf2Instrument(createDefaultDrumSf2Patch());
  }
  if (kind === "sf2") {
    return sf2Instrument(sf2PatchForGmMelodic(program, name));
  }
  return synthInstrument(presetForGmProgram(program, name));
}

function chooseStepDivision(midi: Midi, bpm: number, durationSec: number): number {
  // Prefer 16; bump if many onsets fall mid-16th.
  for (const div of [16, 32, 64] as const) {
    const stepSec = secondsPerStep({ bpm, stepDivision: div });
    let mid = 0;
    let total = 0;
    for (const track of midi.tracks) {
      for (const note of track.notes) {
        if (note.time > durationSec) {
          continue;
        }
        total += 1;
        const frac = (note.time / stepSec) % 1;
        if (frac > 0.12 && frac < 0.88) {
          mid += 1;
        }
      }
    }
    if (total === 0 || mid / total < 0.25 || div === 64) {
      return div;
    }
  }
  return 16;
}

function collectChannels(midi: Midi): ChannelPlan[] {
  const byChannel = new Map<number, ChannelPlan>();

  for (const track of midi.tracks) {
    if (!track.notes.length && Object.keys(track.controlChanges).length === 0) {
      continue;
    }
    const channel = track.channel | 0;
    let plan = byChannel.get(channel);
    if (!plan) {
      plan = {
        channel,
        kind: "synth",
        name: "",
        program: track.instrument.number | 0,
        notes: [],
        cc1: [],
        cc10: [],
        cc74: [],
        bends: [],
      };
      byChannel.set(channel, plan);
    }
    plan.program = track.instrument.number | 0;
    const trackName = (track.name || track.instrument.name || "").trim();
    if (trackName && !plan.name) {
      plan.name = trackName.slice(0, 24);
    }
    for (const note of track.notes) {
      plan.notes.push({
        channel,
        midi: note.midi,
        time: note.time,
        duration: note.duration,
        velocity: note.velocity,
        program: track.instrument.number | 0,
        trackName,
        family: track.instrument.family || "",
        percussion: track.instrument.percussion === true || channel === 9,
      });
    }
    appendCc(plan.cc1, track.controlChanges[1]);
    appendCc(plan.cc10, track.controlChanges[10]);
    appendCc(plan.cc74, track.controlChanges[74]);
    for (const bend of track.pitchBends) {
      plan.bends.push({ time: bend.time, value: bend.value });
    }
  }

  const plans = [...byChannel.values()].filter(p => p.notes.length > 0);
  plans.sort((a, b) => a.channel - b.channel);

  for (const plan of plans) {
    plan.kind = classifyChannel(plan);
    if (!plan.name) {
      plan.name = defaultChannelName(plan);
    }
    plan.cc1.sort((a, b) => a.time - b.time);
    plan.cc10.sort((a, b) => a.time - b.time);
    plan.cc74.sort((a, b) => a.time - b.time);
    plan.bends.sort((a, b) => a.time - b.time);
  }
  return plans;
}

function appendCc(
  dest: TimedValue[],
  events: { time: number; value: number }[] | undefined,
): void {
  if (!events) {
    return;
  }
  for (const ev of events) {
    // @tonejs/midi normalizes CC to 0..1
    dest.push({ time: ev.time, value: clamp01(ev.value) });
  }
}

function classifyChannel(plan: ChannelPlan): LabKind {
  if (plan.channel === 9 || plan.notes.some(n => n.percussion)) {
    return "drums";
  }
  const program = plan.program;
  const name = `${plan.name} ${plan.notes[0]?.trackName ?? ""} ${plan.notes[0]?.family ?? ""}`;
  if (program >= 0 && program <= 7) {
    return "sf2";
  }
  if (program >= 24 && program <= 31) {
    return "sf2";
  }
  if (/piano|keys|grand|upright|epiano|e-piano|electric piano/i.test(name)) {
    return "sf2";
  }
  if (isDistortionGuitarGm(program, name) || isElectricGuitarGm(program, name)) {
    return "sf2";
  }
  if (/^piano$/i.test(plan.notes[0]?.family ?? "")) {
    return "sf2";
  }
  if (/^guitar$/i.test(plan.notes[0]?.family ?? "")) {
    return "sf2";
  }
  return "synth";
}

function defaultChannelName(plan: ChannelPlan): string {
  if (plan.kind === "drums") {
    return "Drums";
  }
  if (plan.kind === "sf2") {
    const name = `${plan.name} ${plan.notes[0]?.trackName ?? ""}`;
    if (isDistortionGuitarGm(plan.program, name)) {
      return "Dist Guitar";
    }
    if (isElectricGuitarGm(plan.program, name)) {
      return "E. Guitar";
    }
    return "Piano";
  }
  return `Ch ${plan.channel + 1}`;
}

function sampleTimeline(events: TimedValue[], time: number, fallback: number): number {
  if (events.length === 0) {
    return fallback;
  }
  let value = fallback;
  for (const ev of events) {
    if (ev.time > time + 1e-9) {
      break;
    }
    value = ev.value;
  }
  return value;
}

function bendAt(events: TimedValue[], time: number): number {
  // pitch bend in tonejs is -1..1
  return sampleTimeline(events, time, 0);
}

function bendToSlideFx(
  bends: TimedValue[],
  t0: number,
  t1: number,
  stepSec: number,
): NoteFx | undefined {
  if (bends.length === 0 || t1 <= t0) {
    return undefined;
  }
  const b0 = bendAt(bends, t0) * DEFAULT_BEND_RANGE_SEMI;
  const b1 = bendAt(bends, t1) * DEFAULT_BEND_RANGE_SEMI;
  const delta = b1 - b0;
  const rate = Math.abs(delta) / Math.max(1e-4, stepSec);
  if (rate < BEND_RATE_THRESHOLD) {
    return undefined;
  }
  // Decode: slideSemiPerSec = max(0.5, v) * 0.55 → v ≈ rate / 0.55
  const xx = Math.min(0xff, Math.max(1, Math.round(rate / 0.55)));
  return { kind: delta >= 0 ? "slideUp" : "slideDown", value: xx };
}

function pushCcFx(fx: NoteFx[], mod: number, pan: number, bright: number): void {
  pushPan(fx, pan);
  pushVibrato(fx, mod);
  pushFilter(fx, bright);
}

function pushVibrato(fx: NoteFx[], mod01: number): void {
  if (mod01 < 0.04) {
    return;
  }
  const depth = Math.min(0xf, Math.max(1, Math.round(mod01 * 15)));
  fx.push({ kind: "vibrato", value: (VIBRATO_SPEED_NIBBLE << 4) | depth });
}

function pushFilter(fx: NoteFx[], bright01: number): void {
  const xx = Math.min(0xff, Math.max(0, Math.round(bright01 * 255)));
  // Omit near-flat center (~0x80)
  if (Math.abs(xx - 0x80) <= 4) {
    return;
  }
  fx.push({ kind: "filter", value: xx });
}

function pushPan(fx: NoteFx[], pan01: number): void {
  const xx = Math.min(0xff, Math.max(0, Math.round(pan01 * 255)));
  if (Math.abs(xx - 0x80) <= 4) {
    return;
  }
  fx.push({ kind: "pan", value: xx });
}

/** Priority: C → E → B → V → F → U/D */
function packFx(fx: NoteFx[]): { fx: NoteFx[]; dropped: number } {
  const priority = (kind: NoteFx["kind"]): number => {
    switch (kind) {
      case "cut":
        return 0;
      case "noteDelay":
        return 1;
      case "pan":
        return 2;
      case "vibrato":
        return 3;
      case "filter":
        return 4;
      case "slideUp":
      case "slideDown":
        return 5;
      default:
        return 9;
    }
  };
  const sorted = [...fx].sort((a, b) => priority(a.kind) - priority(b.kind));
  // Dedupe by kind (keep first = highest priority / first written)
  const seen = new Set<string>();
  const unique: NoteFx[] = [];
  for (const item of sorted) {
    const key = item.kind === "slideUp" || item.kind === "slideDown" ? "slide" : item.kind;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(item);
  }
  if (unique.length <= FX_BUDGET) {
    return { fx: unique, dropped: Math.max(0, fx.length - unique.length) };
  }
  return {
    fx: unique.slice(0, FX_BUDGET),
    dropped: unique.length - FX_BUDGET + Math.max(0, fx.length - unique.length),
  };
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  // Accept 0..127 MIDI or 0..1 normalized
  if (value > 1.0001) {
    return Math.min(1, Math.max(0, value / 127));
  }
  return Math.min(1, Math.max(0, value));
}
